import { useRef, useEffect, useState } from 'react';
import { Send, Paperclip, Images, Loader2, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@iheart/ui';
import { useAuth } from '../../lib/auth';
import {
  useChatMessages,
  useSendMessage,
  useMarkRead,
  useRoomMembers,
  useUploadChatAttachment,
  useSearchRoomMessages,
  type ChatMessage,
  type ChatAttachmentMeta,
} from '../../lib/chat';
import { ChatAttachment } from './ChatAttachment';
import { ChatMediaGallery } from './ChatMediaGallery';

const ACCEPT =
  'image/*,video/*,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip';

interface WidgetThreadProps {
  roomPublicId: string;
}

export function WidgetThread({ roomPublicId }: WidgetThreadProps) {
  const { user } = useAuth();
  const messages = useChatMessages(roomPublicId);
  const send = useSendMessage(roomPublicId);
  const markRead = useMarkRead(roomPublicId);
  const members = useRoomMembers(roomPublicId);
  const upload = useUploadChatAttachment(roomPublicId);
  const [draft, setDraft] = useState('');
  const [showGallery, setShowGallery] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQ, setSearchQ] = useState('');
  const [debSearch, setDebSearch] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const id = setTimeout(() => setDebSearch(searchQ), 200);
    return () => clearTimeout(id);
  }, [searchQ]);
  const search = useSearchRoomMessages(roomPublicId, debSearch);
  const isSearching = searchOpen && debSearch.trim().length >= 2;

  // Auto-scroll to bottom (only in normal thread view)
  useEffect(() => {
    if (!isSearching && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.data, isSearching]);

  // Mark as read when viewing
  useEffect(() => {
    markRead.mutate();
  }, [roomPublicId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleSend() {
    const text = draft.trim();
    if (!text || send.isPending) return;
    setDraft('');
    await send.mutateAsync({ body: text });
    inputRef.current?.focus();
  }

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      await upload.mutateAsync(file);
    } catch {
      toast.error('Upload failed. Please try again.');
    }
  }

  const sorted = [...(messages.data ?? [])].reverse();
  const results = search.data ?? [];

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      {/* Header info + actions */}
      <div className="flex items-center justify-between gap-2 border-b border-neutral-100 px-3 py-1.5">
        <span className="min-w-0 truncate text-[11px] text-neutral-400">
          {members.data && members.data.length > 0
            ? members.data.map((m) => m.name.split(' ')[0]).join(', ')
            : ''}
        </span>
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            onClick={() => {
              setSearchOpen((v) => !v);
              setSearchQ('');
            }}
            title="Search this chat"
            aria-label="Search this chat"
            className={cn(
              'flex items-center gap-1 rounded-md px-1.5 py-1 text-[11px] font-medium transition-colors hover:bg-neutral-100',
              searchOpen ? 'text-pink-600' : 'text-neutral-500 hover:text-neutral-700',
            )}
          >
            <Search className="size-3.5" />
          </button>
          <button
            type="button"
            onClick={() => setShowGallery(true)}
            title="View media & documents shared in this chat"
            className="flex items-center gap-1 rounded-md px-1.5 py-1 text-[11px] font-medium text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-700"
          >
            <Images className="size-3.5" />
            Media &amp; docs
          </button>
        </div>
      </div>

      {/* Search bar */}
      {searchOpen && (
        <div className="flex items-center gap-2 border-b border-neutral-100 bg-neutral-50 px-3 py-2">
          <Search className="size-3.5 shrink-0 text-neutral-400" />
          <input
            autoFocus
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
            placeholder="Search messages in this chat…"
            className="flex-1 bg-transparent text-xs outline-none placeholder:text-neutral-400"
          />
          <button
            type="button"
            onClick={() => {
              setSearchOpen(false);
              setSearchQ('');
            }}
            aria-label="Close search"
            className="shrink-0 rounded-full p-0.5 text-neutral-400 hover:bg-neutral-200 hover:text-neutral-600"
          >
            <X className="size-3.5" />
          </button>
        </div>
      )}

      {/* Messages / search results */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3">
        {isSearching ? (
          search.isLoading ? (
            <p className="py-8 text-center text-xs text-neutral-400">Searching…</p>
          ) : results.length === 0 ? (
            <p className="py-8 text-center text-xs text-neutral-400">No messages match “{debSearch.trim()}”.</p>
          ) : (
            <div className="space-y-2">
              <p className="pb-1 text-[10px] font-medium uppercase tracking-wider text-neutral-400">
                {results.length} result{results.length === 1 ? '' : 's'}
              </p>
              {results.map((msg) => (
                <MessageBubble key={msg.publicId} message={msg} isOwn={msg.senderPublicId === user?.publicId} />
              ))}
            </div>
          )
        ) : !messages.data ? (
          <div className="flex items-center justify-center py-8">
            <div className="size-5 animate-spin rounded-full border-2 border-pink-200 border-t-pink-500" />
          </div>
        ) : sorted.length === 0 ? (
          <p className="py-8 text-center text-xs text-neutral-400">No messages yet. Say hello!</p>
        ) : (
          <div className="space-y-2">
            {sorted.map((msg) => (
              <MessageBubble key={msg.publicId} message={msg} isOwn={msg.senderPublicId === user?.publicId} />
            ))}
          </div>
        )}
      </div>

      {/* Compose */}
      <div className="border-t border-neutral-100 p-2">
        <div className="flex items-end gap-1.5">
          <input ref={fileRef} type="file" accept={ACCEPT} className="hidden" onChange={handleFile} />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={upload.isPending}
            aria-label="Attach a file"
            title="Attach image, video or document"
            className="flex size-9 shrink-0 items-center justify-center rounded-lg border border-neutral-200 text-neutral-500 transition-colors hover:bg-neutral-50 hover:text-neutral-700 disabled:opacity-50"
          >
            {upload.isPending ? <Loader2 className="size-4 animate-spin" /> : <Paperclip className="size-4" />}
          </button>
          <textarea
            ref={inputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            placeholder="Type a message..."
            rows={1}
            className="flex-1 resize-none rounded-lg border border-neutral-200 bg-white px-3 py-2 text-sm outline-none focus:border-pink-300 focus:ring-1 focus:ring-pink-100"
          />
          <button
            type="button"
            onClick={handleSend}
            disabled={!draft.trim() || send.isPending}
            aria-label="Send message"
            title="Send message"
            className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-pink-500 text-white transition-colors hover:bg-pink-600 disabled:opacity-50"
          >
            <Send className="size-4" />
          </button>
        </div>
      </div>

      {showGallery && (
        <ChatMediaGallery roomPublicId={roomPublicId} onClose={() => setShowGallery(false)} />
      )}
    </div>
  );
}

function MessageBubble({ message, isOwn }: { message: ChatMessage; isOwn: boolean }) {
  const time = new Date(message.createdAt).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });

  const meta =
    (message.kind === 'image' || message.kind === 'file') &&
    message.metadata &&
    typeof (message.metadata as { url?: unknown }).url === 'string'
      ? (message.metadata as unknown as ChatAttachmentMeta)
      : null;

  return (
    <div className={cn('flex', isOwn ? 'justify-end' : 'justify-start')}>
      <div className={cn('max-w-[80%] space-y-0.5')}>
        {!isOwn && (
          <span className="text-[10px] font-medium text-neutral-500">
            {message.senderName.split(' ')[0]}
          </span>
        )}
        {meta ? (
          <ChatAttachment meta={meta} />
        ) : (
          <div
            className={cn(
              'rounded-xl px-3 py-2 text-sm',
              isOwn
                ? 'rounded-br-sm bg-pink-500 text-white'
                : 'rounded-bl-sm border border-neutral-200 bg-white text-neutral-800',
            )}
          >
            {message.body}
          </div>
        )}
        <span className={cn('text-[10px] text-neutral-400', isOwn && 'text-right')}>{time}</span>
      </div>
    </div>
  );
}
